/**
 * Manager for configuration validation and merging
 */

import type { z } from "zod";
import {
  type BabylonConfig,
  BabylonConfigSchema,
  defaultBabylonConfig,
  type PartialBabylonConfig,
  PartialBabylonConfigSchema,
  safeValidateBabylonConfig,
  safeValidatePartialBabylonConfig,
} from "../../config/schemas";
import { mergeConfigs } from "../../config/utils";
import { BaseManager } from "../core/BaseManager";
import type { EventBus } from "../core/EventBus";

/**
 * Configuration change event data
 */
interface ConfigChangeEvent {
  previous: BabylonConfig;
  current: BabylonConfig;
  changes: string[];
}

/**
 * Configuration validation error event
 */
interface ConfigValidationErrorEvent {
  error: z.ZodError;
  config: unknown;
}

/**
 * Manages configuration validation, merging, and lifecycle
 */
export class ConfigurationManager extends BaseManager {
  private currentConfig: BabylonConfig;
  private configHistory: BabylonConfig[] = [];
  private maxHistorySize = 10;

  /**
   * Create a new ConfigurationManager
   * @param eventBus The event bus for manager communication
   * @param initialConfig Initial configuration
   */
  constructor(eventBus: EventBus, initialConfig?: PartialBabylonConfig) {
    super(eventBus);
    this.currentConfig = this.createDefaultConfig(initialConfig);
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    // Validate initial configuration
    const result = safeValidateBabylonConfig(this.currentConfig);
    if (!result.success) {
      this.emit("configValidationError", {
        config: this.currentConfig,
        error: result.error,
      } satisfies ConfigValidationErrorEvent);
      throw new Error(`Invalid initial configuration: ${result.error.message}`);
    }

    this.emit("configurationManagerReady", {
      config: this.currentConfig,
    });
  }

  /**
   * Get the current configuration
   */
  getConfig(): BabylonConfig {
    this.ensureInitialized();
    return { ...this.currentConfig };
  }

  /**
   * Validate a configuration without applying it
   * @param config Configuration to validate
   */
  validate(config: unknown): BabylonConfig {
    return BabylonConfigSchema.parse(config);
  }

  /**
   * Safely validate a configuration
   * @param config Configuration to validate
   */
  safeValidate(
    config: unknown,
  ):
    | { success: true; data: BabylonConfig }
    | { success: false; error: z.ZodError } {
    return safeValidateBabylonConfig(config);
  }

  /**
   * Validate a partial configuration
   * @param config Partial configuration to validate
   */
  validatePartial(config: unknown): PartialBabylonConfig {
    return PartialBabylonConfigSchema.parse(config);
  }

  /**
   * Safely validate a partial configuration
   * @param config Partial configuration to validate
   */
  safeValidatePartial(
    config: unknown,
  ):
    | { success: true; data: PartialBabylonConfig }
    | { success: false; error: z.ZodError } {
    return safeValidatePartialBabylonConfig(config);
  }

  /**
   * Update the configuration with a partial update
   * @param updates Partial configuration updates
   */
  updateConfig(updates: PartialBabylonConfig): void {
    this.ensureInitialized();

    // Validate partial updates
    const validatedUpdates = this.validatePartial(updates);

    // Store previous config
    const previousConfig = this.currentConfig;
    this.addToHistory(previousConfig);

    // Merge configurations
    const merged = mergeConfigs(this.currentConfig, validatedUpdates);

    // Validate merged configuration
    const result = this.safeValidate(merged);
    if (!result.success) {
      this.emit("configValidationError", {
        config: merged,
        error: result.error,
      } satisfies ConfigValidationErrorEvent);
      throw new Error(
        `Invalid configuration after merge: ${result.error.message}`,
      );
    }

    // Apply new configuration
    this.currentConfig = result.data;

    // Calculate changes
    const changes = this.calculateChanges(previousConfig, this.currentConfig);

    // Emit change event
    this.emit("configChanged", {
      changes,
      current: this.currentConfig,
      previous: previousConfig,
    } satisfies ConfigChangeEvent);
  }

  /**
   * Replace the entire configuration
   * @param config New configuration
   */
  setConfig(config: BabylonConfig): void {
    this.ensureInitialized();

    // Validate new configuration
    const validatedConfig = this.validate(config);

    // Store previous config
    const previousConfig = this.currentConfig;
    this.addToHistory(previousConfig);

    // Apply new configuration
    this.currentConfig = validatedConfig;

    // Calculate changes
    const changes = this.calculateChanges(previousConfig, this.currentConfig);

    // Emit change event
    this.emit("configChanged", {
      changes,
      current: this.currentConfig,
      previous: previousConfig,
    } satisfies ConfigChangeEvent);
  }

  /**
   * Reset configuration to defaults
   */
  resetToDefaults(): void {
    this.ensureInitialized();
    this.setConfig(defaultBabylonConfig);
  }

  /**
   * Get configuration history
   */
  getHistory(): ReadonlyArray<BabylonConfig> {
    return [...this.configHistory];
  }

  /**
   * Get a specific configuration from history
   * @param index History index (0 is most recent)
   */
  getFromHistory(index: number): BabylonConfig | undefined {
    return this.configHistory[index];
  }

  /**
   * Restore configuration from history
   * @param index History index to restore
   */
  restoreFromHistory(index: number): void {
    const config = this.getFromHistory(index);
    if (!config) {
      throw new Error(`No configuration found at history index ${index}`);
    }
    this.setConfig(config);
  }

  /**
   * Clear configuration history
   */
  clearHistory(): void {
    this.configHistory = [];
  }

  /**
   * Set maximum history size
   * @param size Maximum number of configurations to keep in history
   */
  setMaxHistorySize(size: number): void {
    if (size < 0) {
      throw new Error("History size must be non-negative");
    }
    this.maxHistorySize = size;
    this.trimHistory();
  }

  /**
   * Create a configuration snapshot
   */
  createSnapshot(): BabylonConfig {
    this.ensureInitialized();
    return JSON.parse(JSON.stringify(this.currentConfig));
  }

  /**
   * Merge multiple configurations
   * @param base Base configuration
   * @param overrides Configurations to merge (in order)
   */
  merge(
    base: BabylonConfig,
    ...overrides: PartialBabylonConfig[]
  ): BabylonConfig {
    let result = base;

    for (const override of overrides) {
      const validatedOverride = this.validatePartial(override);
      result = mergeConfigs(result, validatedOverride);
    }

    return this.validate(result);
  }

  /**
   * Create default configuration with optional overrides
   */
  private createDefaultConfig(overrides?: PartialBabylonConfig): BabylonConfig {
    if (!overrides) {
      return defaultBabylonConfig;
    }

    const validatedOverrides = PartialBabylonConfigSchema.parse(overrides);
    const merged = mergeConfigs(defaultBabylonConfig, validatedOverrides);
    return BabylonConfigSchema.parse(merged);
  }

  /**
   * Add configuration to history
   */
  private addToHistory(config: BabylonConfig): void {
    this.configHistory.unshift(config);
    this.trimHistory();
  }

  /**
   * Trim history to max size
   */
  private trimHistory(): void {
    if (this.configHistory.length > this.maxHistorySize) {
      this.configHistory = this.configHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Calculate changes between configurations
   */
  private calculateChanges(prev: BabylonConfig, curr: BabylonConfig): string[] {
    const changes: string[] = [];

    // Check top-level properties
    const topLevelKeys = new Set([
      ...Object.keys(prev),
      ...Object.keys(curr),
    ]) as Set<keyof BabylonConfig>;

    for (const key of topLevelKeys) {
      if (JSON.stringify(prev[key]) !== JSON.stringify(curr[key])) {
        changes.push(key);
      }
    }

    return [...new Set(changes)].sort();
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this.configHistory = [];
  }
}
